import type { CSSProperties, ReactNode } from "react";
import { COLORS } from '../lib/theme';
import { Icon, type IconName } from "./Icon";

export function PageHeader({
  title,
  titleBadge,
  subtitle,
  actions,
}: {
  title: string;
  /** A small element shown right of the title (e.g. an estate tag), so it holds for the whole page. */
  titleBadge?: ReactNode;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div className="wh-page-header-row" style={{ display: "flex", alignItems: "flex-end", gap: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <h1
              style={{
                fontFamily: COLORS.fontDisplay,
                fontWeight: 500,
                fontSize: 32,
                margin: 0,
                lineHeight: 1.1,
                color: COLORS.onSurface,
              }}
            >
              {title}
            </h1>
            {titleBadge}
          </div>
          {subtitle && (
            <div style={{ marginTop: 6, fontSize: 14, color: COLORS.outline }}>{subtitle}</div>
          )}
        </div>
        {actions && <div style={{ display: "flex", gap: 8 }}>{actions}</div>}
      </div>
    </div>
  );
}

export function SectionHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 12 }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.onSurface }}>{title}</div>
        {subtitle && (
          <div style={{ fontSize: 12, color: COLORS.outline, marginTop: 2 }}>{subtitle}</div>
        )}
      </div>
      {action}
    </div>
  );
}

export function StatCard({
  icon,
  label,
  value,
  unit,
  delta,
  warn,
}: {
  icon: IconName;
  label: string;
  value: string | number;
  unit?: string;
  delta?: string;
  warn?: boolean;
}) {
  return (
    <div className="wh-card" style={{ padding: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: warn ? COLORS.tertiaryContainer : COLORS.primaryContainer,
            // on-*Container pairs with the container in both themes; the *FixedVariant
            // is #a03c16 in both, i.e. orange-on-orange against tertiaryContainer on dark.
            color: warn ? COLORS.onTertiaryContainer : COLORS.primary,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon name={icon} size={18} />
        </div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: COLORS.outline,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          {label}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <div
          style={{
            fontFamily: COLORS.fontDisplay,
            fontSize: 32,
            fontWeight: 700,
            color: COLORS.onSurface,
            lineHeight: 1,
          }}
        >
          {value}
        </div>
        {unit && (
          <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.outline }}>{unit}</div>
        )}
      </div>
      {delta && (
        <div
          style={{
            fontSize: 12,
            color: warn ? COLORS.onTertiaryFixedVariant : COLORS.outline,
            marginTop: 6,
          }}
        >
          {delta}
        </div>
      )}
    </div>
  );
}

export function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          color: COLORS.outline,
          textTransform: "uppercase",
          letterSpacing: 0.3,
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.onSurface }}>{value}</div>
    </div>
  );
}

export function ActivityChart() {
  const data = [3, 5, 2, 8, 6, 9, 4];
  const max = 10;
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "flex-end", height: 100 }}>
      {data.map((v, i) => (
        <div
          key={`day-${i}`}
          style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}
        >
          <div
            style={{
              width: "100%",
              height: `${(v / max) * 80 + 8}px`,
              background: i === 5 ? COLORS.primary : COLORS.primaryFixedDim,
              borderRadius: 4,
            }}
          />
          <div style={{ fontSize: 10, color: COLORS.outline }}>{days[i]}</div>
        </div>
      ))}
    </div>
  );
}

export function DogRow({
  dog,
  showDivider,
}: {
  dog: { name: string; breed: string; age: number; color: string; status: "tracking" | "idle" };
  showDivider?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 0",
        borderTop: showDivider ? `1px solid ${COLORS.surfaceContainerHighest}` : "none",
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 999,
          background: dog.color,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon name="dog" size={22} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>{dog.name}</div>
        <div style={{ fontSize: 12, color: COLORS.outline }}>
          {dog.breed} · {dog.age} yrs
        </div>
      </div>
      <span
        className={`wh-chip ${dog.status === "tracking" ? "" : "muted"}`}
        style={
          dog.status === "tracking"
            ? undefined
            : { background: COLORS.surfaceContainer, color: COLORS.onSurfaceVariant }
        }
      >
        {dog.status === "tracking" ? "● Active" : "Resting"}
      </span>
    </div>
  );
}

export function BatteryIndicator({ pct }: { pct: number }) {
  const color = pct > 50 ? COLORS.primary : pct > 20 ? COLORS.tertiary : COLORS.error;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div
        style={{
          width: 28,
          height: 14,
          borderRadius: 2,
          border: `1.5px solid ${color}`,
          position: "relative",
          padding: 1,
        }}
      >
        <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 1 }} />
        <div
          style={{
            position: "absolute",
            right: -3,
            top: 3,
            bottom: 3,
            width: 2,
            background: color,
            borderRadius: 1,
          }}
        />
      </div>
      <span style={{ fontSize: 12, fontWeight: 700, color, minWidth: 32 }}>{pct}%</span>
    </div>
  );
}

export function DeviceMiniRow({
  device,
  showDivider,
}: {
  device: { name: string; battery: number; paired: string | null; lastSeen: string };
  showDivider?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 0",
        borderTop: showDivider ? `1px solid ${COLORS.surfaceContainerHighest}` : "none",
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 8,
          background: COLORS.primaryContainer,
          color: COLORS.primary,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon name="device" size={20} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>{device.name}</div>
        <div style={{ fontSize: 12, color: COLORS.outline }}>
          {device.paired ? `Paired with ${device.paired}` : "Not paired"} · {device.lastSeen}
        </div>
      </div>
      <BatteryIndicator pct={device.battery} />
    </div>
  );
}

export function Avatar({
  initials,
  photo,
  size = 36,
}: {
  initials: string;
  photo?: string;
  size?: number;
}) {
  const base: CSSProperties = {
    width: size,
    height: size,
    borderRadius: 999,
    flexShrink: 0,
    overflow: "hidden",
  };

  if (photo) {
    return (
      <div style={base}>
        <img
          src={photo.startsWith("http") || photo.startsWith("/") ? photo : `/assets/${photo}`}
          alt={initials}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        ...base,
        background: COLORS.surfaceDim,
        border: `1.5px solid ${COLORS.outlineVariant}`,
        color: COLORS.onSurface,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: Math.round(size * 0.36),
        fontWeight: 700,
      }}
    >
      {initials}
    </div>
  );
}
